import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { Image, Text, Box } from '@user-portal/wikr-components';

import installApp from 'appConfig/installApp';

import { selectCurrentUser } from 'store/user/selectors';
import { createProcessingSelector } from 'store/loading/selectors';
import { selectDeepLink } from 'store/deepLink/selectors';
import { GET_DEEP_LINK } from 'store/deepLink/actionTypes';
import { getDeepLinkRequest } from 'store/deepLink/actions';

import { HIDE_FRONT_CHAT_VALUE } from 'constants/frontChat';

import { showFrontChat } from 'services/FrontChat';

import { useScreenLoadTracker, useSelector } from 'hooks';

import isMobile from 'helpers/isMobile';

import Skeleton from 'components/Skeleton';
import ContactUsLink from 'components/ContactUsLink';

import * as S from './styled';

import Success from './img/Success.svg';
import logo from './img/projectLogo.webp';
import phone from './img/phone.webp';
import Check from './img/Check.svg';
import bigStore from './img/bigStore.webp';
import bigGoogle from './img/bigGoogle.webp';
import Arrow from './img/Arrow.svg';

import { getMobileContent, getDesktopContent } from './config';

import { isTabletDevice, isSmallDesktopDevice } from 'theme/verifiers';
import {
    trackMainAppStoreClick,
    trackMainGooglePayClick,
    trackMainSupportClick,
    trackSimpleLoginChatClick,
    trackSimpleLoginPageLoad,
} from 'analytics/trackers/installApp';

export const LoginInstructionSimple = () => {
    useScreenLoadTracker(() => trackSimpleLoginPageLoad());

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isLoading: boolean = useSelector(createProcessingSelector([GET_DEEP_LINK]));
    const deepLinkUrl = useSelector(selectDeepLink);
    const user = useSelector(selectCurrentUser);

    const isIos = isMobile.iOS();
    const isAndroid = isMobile.android();

    const mobileInstructions = getMobileContent(Boolean(isIos));
    const desktopInstructions = getDesktopContent();

    useEffect(() => {
        if (!deepLinkUrl) dispatch(getDeepLinkRequest());
    }, []);

    const displayFrontChat = () => {
        trackSimpleLoginChatClick();

        showFrontChat();
    };

    const handleDesktopGoogle = () => {
        trackMainGooglePayClick();

        // Workaround for window.open method since it doesn't work for Safari browser
        Object.assign(document.createElement('a'), {
            target: '_blank',
            rel: 'noopener noreferrer',
            href: installApp.googlePlayAppLink,
        }).click();
    };

    const handleDesktopStore = () => {
        trackMainAppStoreClick();

        // Workaround for window.open method since it doesn't work for Safari browser
        Object.assign(document.createElement('a'), {
            target: '_blank',
            rel: 'noopener noreferrer',
            href: installApp.appStoreAppLink,
        }).click();
    };

    const handleDownloadApp = () => {
        if (isAndroid) trackMainGooglePayClick();
        if (isIos) trackMainAppStoreClick();

        // Workaround for window.open method since it doesn't work for Safari browser
        Object.assign(document.createElement('a'), {
            target: '_blank',
            rel: 'noopener noreferrer',
            href: deepLinkUrl,
        }).click();
    };

    const isFrontChatAvailable = import.meta.env.VITE_HIDE_FRONT_CHAT !== HIDE_FRONT_CHAT_VALUE;

    return (
        <>
            <S.Container>
                {isTabletDevice ? (
                    <Box>
                        <Text medium type={isSmallDesktopDevice ? 'h2' : 'h3'} mb={32}>
                            {t('downloadPage.letsStart')}
                        </Text>
                        <S.ContentWrapper $backgroundColor="surface-default" $borderRadius={20} $mb={32}>
                            <S.InstructionsWrapper>
                                {desktopInstructions.data.map((instruction, index) => (
                                    <S.InstructionContainer key={index}>
                                        <S.InstructionNumberContainer>
                                            <S.InstructionNumber>
                                                <Text type="large-text" bold>
                                                    {index + 1}
                                                </Text>
                                            </S.InstructionNumber>
                                        </S.InstructionNumberContainer>
                                        <S.Instruction>
                                            <Text type="large-text">
                                                <Trans
                                                    i18nKey={instruction.text}
                                                    values={{ email: user.email }}
                                                    components={{ Bold: <S.LargeText /> }}
                                                />
                                            </Text>
                                            {instruction?.disclaimer && (
                                                <Text color="text-secondary" mt={8}>
                                                    {t(instruction.disclaimer)}
                                                </Text>
                                            )}
                                        </S.Instruction>
                                    </S.InstructionContainer>
                                ))}
                                <S.InstructionDownloadBlock>
                                    <S.Line />
                                    <Text medium type="h5" center mb={24}>
                                        {t('downloadPage.downloadStore')}
                                    </Text>
                                    <S.ButtonsWrapper>
                                        <S.Link onClick={handleDesktopStore}>
                                            <Image src={bigStore} alt="store" maxWidth={192} />
                                        </S.Link>
                                        <S.Link onClick={handleDesktopGoogle}>
                                            <Image src={bigGoogle} alt="google" maxWidth={216} />
                                        </S.Link>
                                    </S.ButtonsWrapper>
                                </S.InstructionDownloadBlock>
                            </S.InstructionsWrapper>
                            <S.ImageWrapper>
                                <Image maxWidth={390} src={phone} alt="phone" center />
                                <S.ArrowContainer>
                                    <Arrow />
                                </S.ArrowContainer>
                                <S.DownloadWrapper>
                                    <S.ScanQrCodeWrapper>
                                        <S.QRWrapper>
                                            {isLoading ? (
                                                <Skeleton borderRadius={8} width={147} height={147} />
                                            ) : (
                                                <QRCodeSVG value={deepLinkUrl} />
                                            )}
                                        </S.QRWrapper>
                                    </S.ScanQrCodeWrapper>
                                </S.DownloadWrapper>
                            </S.ImageWrapper>
                            <S.DownloadBlock>
                                <Text type="h5" mb={24} center>
                                    {t('downloadPage.downloadStore')}
                                </Text>
                                <S.ButtonsWrapper>
                                    <S.Link onClick={handleDesktopStore}>
                                        <Image src={bigStore} alt="store" maxWidth={192} />
                                    </S.Link>
                                    <S.Link onClick={handleDesktopGoogle}>
                                        <Image src={bigGoogle} alt="google" maxWidth={216} />
                                    </S.Link>
                                </S.ButtonsWrapper>
                            </S.DownloadBlock>
                        </S.ContentWrapper>
                    </Box>
                ) : (
                    <Box>
                        <Text type="h4" mb={32} center>
                            {t('downloadPage.letsStart')}
                        </Text>
                        <Box
                            $backgroundColor="surface-default"
                            $paddingX={20}
                            $paddingBottom={24}
                            $paddingTop={24}
                            $mb={16}
                            $borderRadius={12}
                        >
                            <S.InstructionsWrapper>
                                {mobileInstructions.data.map((instruction, index) => (
                                    <S.InstructionContainer key={index}>
                                        <S.InstructionNumberContainer>
                                            <S.InstructionNumber>
                                                <Text type="large-text" bold>
                                                    {index + 1}
                                                </Text>
                                            </S.InstructionNumber>
                                        </S.InstructionNumberContainer>
                                        <S.Instruction>
                                            <Text mb={instruction?.image ? 12 : 0} type="medium-text">
                                                <Trans
                                                    i18nKey={instruction.text}
                                                    values={{ email: user.email }}
                                                    components={{ Bold: <S.LargeText /> }}
                                                />
                                                {instruction?.image && ':'}
                                            </Text>
                                            {instruction?.image && (
                                                <S.Link onClick={handleDownloadApp} data-locator="DeeplinkBtn">
                                                    <Image src={instruction.image} alt="link" maxWidth={162} />
                                                </S.Link>
                                            )}
                                            {instruction?.disclaimer && (
                                                <Text color="text-secondary" type="small-text" mt={8}>
                                                    {t(instruction.disclaimer)}
                                                </Text>
                                            )}
                                        </S.Instruction>
                                    </S.InstructionContainer>
                                ))}
                            </S.InstructionsWrapper>
                        </Box>
                    </Box>
                )}
                <S.HelpBox
                    $backgroundColor="info-surface"
                    $borderRadius={20}
                    $paddingX={20}
                    $paddingTop={16}
                    $paddingBottom={16}
                >
                    <S.IconBox>
                        <Check />
                    </S.IconBox>
                    <Box>
                        <Text>{t('downloadPage.havingTrouble')}</Text>
                        <ContactUsLink type="medium-text" withUnderline leftAligned onClick={trackMainSupportClick} />
                    </Box>
                </S.HelpBox>
                <S.HelpBox
                    $backgroundColor="success-surface"
                    $borderRadius={20}
                    $paddingX={20}
                    $paddingTop={16}
                    $paddingBottom={16}
                >
                    <S.IconBox>
                        <Success />
                    </S.IconBox>
                    <Text>{t('downloadPage.payAgain')}</Text>
                </S.HelpBox>
                <S.Footer $paddingBottom={56} $backgroundColor="surface-main">
                    <Text center color="text-secondary" type="caption" medium>
                        {t('basics.made')}
                    </Text>
                    <Image maxWidth={120} src={logo} alt="logo" center />
                </S.Footer>
            </S.Container>
            {isFrontChatAvailable && (
                <Text type="small-text" center color="text-secondary">
                    <Trans
                        i18nKey="loginInstructionSimple.subtitle"
                        components={{ Button: <S.FrontChatButton onClick={displayFrontChat} /> }}
                    />
                </Text>
            )}
        </>
    );
};
